import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Save } from '@factorialco/f0-react/icons/app'
import { PoTemplateDetailDocument, SavePoTemplateDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import FieldEditor from 'modules/procurement/components/poTemplates/FieldEditor'
import type { NumerationState } from 'modules/procurement/components/poTemplates/FieldEditor'
import { mergeFields } from 'modules/procurement/components/poTemplates/FieldEditor/mergeFields'
import {
  toCustomFieldType,
  type MergedField,
  type SectionType,
} from 'modules/procurement/components/poTemplates/FieldEditor/types'

function PoTemplateDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const [localVisibility, setLocalVisibility] = useState<Map<string, boolean> | null>(null)
  const [localCustomFields, setLocalCustomFields] = useState<MergedField[]>([])
  const [numeration, setNumeration] = useState<NumerationState>({ prefix: '', format: '' })
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Fetch template detail + predefined fields catalog in a single query
  const {
    data: detailData,
    loading,
    error: detailError,
  } = useQuery(PoTemplateDetailDocument, {
    variables: { poTemplateId: id ?? '' },
    skip: !id,
  })

  const [savePoTemplate, { loading: isSaving }] = useMutation(SavePoTemplateDocument, {
    extractorFn: (data) => data.procurementMutations.saveProcurementPoTemplate,
    refetchQueries: [PoTemplateDetailDocument],
  })

  const poTemplate = detailData?.procurement?.poTemplatesConnection?.edges?.[0]?.node
  const catalogFields = useMemo(
    () =>
      detailData?.procurement?.poTemplatePredefinedFieldsConnection?.edges?.map((e) => e.node) ??
      [],
    [detailData]
  )

  // Sync numeration from server data when it loads
  useEffect(() => {
    if (poTemplate) {
      setNumeration({
        prefix: poTemplate.poNumberPrefix ?? '',
        format: poTemplate.poNumberFormat ?? '',
      })
    }
  }, [poTemplate])

  const currentVersion = useMemo(() => {
    if (!poTemplate) return undefined
    const versions = poTemplate.poTemplateVersionsConnection.edges.map((e) => e.node)
    return versions.find((v) => v.status === 'draft') ?? versions.find((v) => v.status === 'active')
  }, [poTemplate])

  const serverFieldDefinitions = useMemo(
    () => currentVersion?.poTemplateFieldDefinitionsConnection.edges.map((e) => e.node) ?? [],
    [currentVersion]
  )

  // Merge predefined catalog with stored definitions
  const mergedFields = useMemo(
    () => mergeFields(catalogFields, serverFieldDefinitions),
    [catalogFields, serverFieldDefinitions]
  )

  // Combine server-merged fields with locally-added custom fields, then apply visibility overrides
  const fieldsWithLocalState = useMemo(() => {
    const all = [...mergedFields, ...localCustomFields]
    if (!localVisibility) return all
    return all.map((f) => {
      const override = localVisibility.get(f.key)
      if (override !== undefined) {
        return { ...f, visible: override }
      }
      return f
    })
  }, [mergedFields, localCustomFields, localVisibility])

  const handleToggleVisibility = useCallback((fieldKey: string, newVisible: boolean) => {
    setLocalVisibility((prev) => {
      const next = new Map(prev ?? [])
      next.set(fieldKey, newVisible)
      return next
    })
    setHasLocalChanges(true)
  }, [])

  const handleAddCustomField = useCallback(
    (section: SectionType, label: string, fieldType: 'text' | 'select', options: string[]) => {
      const fieldKey = crypto.randomUUID()
      const sectionFields = fieldsWithLocalState.filter((f) => f.sectionType === section)
      const maxPosition = sectionFields.reduce((max, f) => Math.max(max, f.position), -1)

      const newField: MergedField = {
        key: `custom:${fieldKey}`,
        sectionType: section,
        fieldKey,
        displayLabel: label,
        fieldType,
        predefined: false,
        visible: true,
        required: false,
        computed: false,
        position: maxPosition + 1,
        storedId: null,
        defaultValue: null,
        options,
      }

      setLocalCustomFields((prev) => [...prev, newField])
      setHasLocalChanges(true)
    },
    [fieldsWithLocalState]
  )

  const handleNumerationChange = useCallback((next: NumerationState) => {
    setNumeration(next)
    setHasLocalChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!poTemplate || !id) return

    const action = await savePoTemplate({
      variables: {
        id,
        fieldDefinitions: fieldsWithLocalState.map((f) => ({
          sectionType: f.sectionType,
          fieldKey: f.fieldKey,
          visible: f.visible,
          position: f.position,
          label: f.predefined ? undefined : f.displayLabel,
          fieldType: f.predefined ? undefined : toCustomFieldType(f.fieldType),
          defaultValue: f.defaultValue ?? undefined,
          options: f.options.length > 0 ? f.options : undefined,
        })),
        poNumberPrefix: numeration.prefix || undefined,
        poNumberFormat: numeration.format || undefined,
        poNumberResetYearly: poTemplate.poNumberResetYearly,
      },
    })

    const result = handleResult(action, (errors) => {
      if (Object.keys(errors).length) {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({ type: 'wrong', message: errorMessages })
      }
    })

    if (result.success) {
      setLocalVisibility(null)
      setLocalCustomFields([])
      setHasLocalChanges(false)
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.settings.po_templates.detail.save_success'),
      })
    }
  }, [poTemplate, id, fieldsWithLocalState, numeration, savePoTemplate])

  return (
    <ResponsivePage.Body
      authorized
      error={detailError}
      loading={loading}
      showHeader={false}
      variant="fullscreen"
    >
      <ResourceHeader
        title={poTemplate?.name ?? ''}
        secondaryActions={[
          {
            label: i18n.t('common.save'),
            onClick: handleSave,
            disabled: !hasLocalChanges || isSaving,
            icon: Save,
          },
        ]}
      />

      <FieldEditor
        fields={fieldsWithLocalState}
        numeration={numeration}
        onToggleVisibility={handleToggleVisibility}
        onAddCustomField={handleAddCustomField}
        onNumerationChange={handleNumerationChange}
      />
    </ResponsivePage.Body>
  )
}

export default PoTemplateDetailPageContent
